import {
	TICKET_AUTO_CLOSE_AFTER_DAYS,
	Ticket,
	TicketAttachmentMeta,
	TicketComment,
	TicketCommentKind,
	TicketDetail,
	TicketStatus,
	ticketStatusChangeSchema,
	TicketTranscriptEntry,
	ticketTranscriptSchema,
	TicketUserRef,
} from "@signalist/shared-types";

/** What the thread renders, in comment order. Internal notes appear only when asked for (staff). */
export type TicketThreadItem =
	| { kind: "message"; comment: TicketComment; isStaff: boolean; isInternal: boolean }
	| { kind: "transcript"; comment: TicketComment; entries: TicketTranscriptEntry[] }
	| { kind: "received"; id: number; at: string }
	| { kind: "status"; id: number; at: string; to: TicketStatus };

/**
 * A Signalist reply is one whose author belongs to another company than the ticket (staff work from
 * company 1). A colleague — a company admin answering on a teammate's ticket — shares the company
 * and renders as a customer message (PREC-3938 decision 9).
 */
export const isStaffAuthor = (
	ticket: Pick<Ticket, "companyId">,
	author: TicketUserRef | null
): boolean => author?.companyId != null && author.companyId !== ticket.companyId;

const parseJson = (body: string): unknown => {
	try {
		return JSON.parse(body);
	} catch {
		return null;
	}
};

const toItem = (ticket: TicketDetail, comment: TicketComment): TicketThreadItem | null => {
	if (comment.kind === TicketCommentKind.MESSAGE) {
		return {
			kind: "message",
			comment,
			isStaff: isStaffAuthor(ticket, comment.author),
			isInternal: comment.isInternal,
		};
	}

	if (comment.kind === TicketCommentKind.TRANSCRIPT) {
		const entries = ticketTranscriptSchema.safeParse(parseJson(comment.body));

		return entries.success ? { kind: "transcript", comment, entries: entries.data } : null;
	}

	const change = ticketStatusChangeSchema.safeParse(parseJson(comment.body));

	if (!change.success) {
		return null;
	}

	// The row written at creation (`from: null`) is the mockup's "Ticket received" line.
	return change.data.from === null
		? { kind: "received", id: comment.id, at: comment.createdAt }
		: { kind: "status", id: comment.id, at: comment.createdAt, to: change.data.to };
};

export interface BuildTicketThreadOptions {
	/** Staff see internal notes (PREC-3940); the customer detail never passes this. */
	includeInternal?: boolean;
}

/** The visible thread. A malformed transcript / status body is skipped, never thrown on. */
export const buildTicketThread = (
	ticket: TicketDetail,
	{ includeInternal = false }: BuildTicketThreadOptions = {}
): TicketThreadItem[] =>
	ticket.comments
		.filter((comment) => includeInternal || !comment.isInternal)
		.map((comment) => toItem(ticket, comment))
		.filter((item): item is TicketThreadItem => item !== null);

/** Who marked the ticket resolved: the author of the last STATUS_CHANGE into RESOLVED (null = cron). */
export const ticketResolvedBy = (ticket: TicketDetail): TicketUserRef | null => {
	const resolvedRows = ticket.comments.filter((comment) => {
		if (comment.kind !== TicketCommentKind.STATUS_CHANGE) {
			return false;
		}

		const change = ticketStatusChangeSchema.safeParse(parseJson(comment.body));

		return change.success && change.data.to === TicketStatus.RESOLVED;
	});

	return resolvedRows.at(-1)?.author ?? null;
};

export const ticketAutoCloseDate = (ticket: Pick<Ticket, "resolvedAt">): Date | null =>
	ticket.resolvedAt
		? new Date(Date.parse(ticket.resolvedAt) + TICKET_AUTO_CLOSE_AFTER_DAYS * 24 * 60 * 60 * 1000)
		: null;

/** Every file on the ticket — body-level plus each comment's — oldest first, for the rail. */
export const allTicketAttachments = (ticket: TicketDetail): TicketAttachmentMeta[] =>
	[...ticket.attachments, ...ticket.comments.flatMap((comment) => comment.attachments)].sort(
		(a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt)
	);

export const ticketUserName = (user: TicketUserRef | null): string =>
	[user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email || "";

export const ticketUserFirstName = (user: TicketUserRef | null): string =>
	user?.firstName || ticketUserName(user);

export const ticketUserInitials = (user: TicketUserRef | null): string => {
	const initials = [user?.firstName, user?.lastName]
		.filter((part): part is string => Boolean(part))
		.map((part) => part[0].toUpperCase())
		.join("");

	return initials || "?";
};

export const isImageAttachment = (attachment: Pick<TicketAttachmentMeta, "mimeType">): boolean =>
	attachment.mimeType.startsWith("image/");
